import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, MapPin } from "lucide-react";
import api from "../lib/api";
import { PROPERTY_TYPES, FURNISHING, AMENITIES, SORT_OPTIONS, LISTING_TYPES } from "../lib/constants";
import PropertyCard from "../components/PropertyCard";
import Seo from "../components/Seo";

const PRICE_STEPS = [
  { label: "No Min", value: "" }, { label: "₹10 L", value: "1000000" }, { label: "₹25 L", value: "2500000" },
  { label: "₹50 L", value: "5000000" }, { label: "₹1 Cr", value: "10000000" }, { label: "₹2 Cr", value: "20000000" },
  { label: "₹5 Cr", value: "50000000" },
];

const Inventory = () => {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const get = (k, d = "") => sp.get(k) || d;
  const update = (obj) => {
    const next = new URLSearchParams(sp);
    Object.entries(obj).forEach(([k, v]) => { v ? next.set(k, v) : next.delete(k); });
    if (!("page" in obj)) next.delete("page");
    setSp(next);
  };

  const amenities = get("amenities") ? get("amenities").split(",") : [];
  const toggleAmenity = (a) => {
    const set = new Set(amenities);
    set.has(a) ? set.delete(a) : set.add(a);
    update({ amenities: [...set].join(",") });
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(sp.entries());
    params.limit = 12;
    api.get("/properties", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [sp]);

  useEffect(() => { fetchData(); window.scrollTo(0, 0); }, [fetchData]);

  const clearAll = () => setSp(new URLSearchParams());
  const activeCount = [...sp.keys()].filter((k) => !["sort", "page"].includes(k)).length;

  const FilterPanel = () => (
    <div className="space-y-7" data-testid="filter-panel">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Looking to</p>
        <div className="flex gap-2">
          {LISTING_TYPES.map((t) => (
            <button key={t.value} data-testid={`filter-listing-${t.value}`}
              onClick={() => update({ listing_type: get("listing_type") === t.value ? "" : t.value })}
              className={`flex-1 rounded-lg py-2 text-sm font-medium border transition-colors ${
                get("listing_type") === t.value ? "bg-brand text-white border-brand" : "border-stone-200 text-stone-600 hover:border-brand"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Property Type</p>
        <select value={get("property_type")} onChange={(e) => update({ property_type: e.target.value })}
          data-testid="filter-property-type"
          className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-brand">
          <option value="">All Types</option>
          {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Locality</p>
        <input value={get("locality")} onChange={(e) => update({ locality: e.target.value })}
          placeholder="e.g. Mansarovar" data-testid="filter-locality"
          className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-brand" />
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Budget</p>
        <div className="flex gap-2">
          <select value={get("min_price")} onChange={(e) => update({ min_price: e.target.value })} data-testid="filter-min-price"
            className="flex-1 rounded-lg border border-stone-200 px-2 py-2.5 text-sm outline-none focus:border-brand">
            {PRICE_STEPS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={get("max_price")} onChange={(e) => update({ max_price: e.target.value })} data-testid="filter-max-price"
            className="flex-1 rounded-lg border border-stone-200 px-2 py-2.5 text-sm outline-none focus:border-brand">
            <option value="">No Max</option>
            {PRICE_STEPS.slice(1).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Bedrooms (min)</p>
        <div className="flex gap-2 flex-wrap">
          {["", "1", "2", "3", "4"].map((b) => (
            <button key={b} onClick={() => update({ bedrooms: get("bedrooms") === b ? "" : b })} data-testid={`filter-bhk-${b || "any"}`}
              className={`rounded-lg px-3.5 py-2 text-sm font-medium border transition-colors ${
                get("bedrooms") === b ? "bg-brand text-white border-brand" : "border-stone-200 text-stone-600 hover:border-brand"}`}>
              {b ? `${b}+ BHK` : "Any"}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Furnishing</p>
        <select value={get("furnishing")} onChange={(e) => update({ furnishing: e.target.value })} data-testid="filter-furnishing"
          className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-brand">
          <option value="">Any</option>
          {FURNISHING.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-3">Amenities</p>
        <div className="flex flex-wrap gap-2">
          {AMENITIES.map((a) => (
            <button key={a} onClick={() => toggleAmenity(a)} data-testid={`filter-amenity-${a}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                amenities.includes(a) ? "bg-brand text-white border-brand" : "border-stone-200 text-stone-600 hover:border-brand"}`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      <button onClick={clearAll} data-testid="filter-clear"
        className="w-full rounded-lg border border-stone-300 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors">
        Clear all filters
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <Seo
        title="Properties in Jaipur — Buy & Rent Flats, Villas, Plots | Satyam Real Estate"
        description="Browse verified property listings in Jaipur. Filter by locality, type, budget, BHK and amenities. Flats, villas, plots, commercial spaces and land."
      />
      <nav className="text-sm text-stone-500 mb-4" data-testid="breadcrumb">
        <Link to="/" className="hover:text-brand">Home</Link> <span className="mx-1.5">/</span>
        <span className="text-stone-800">Properties</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Property Inventory</h1>
          <p className="text-stone-500 mt-1" data-testid="results-count">{data.total} properties available in Jaipur</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-btn"
            className="lg:hidden flex items-center gap-2 rounded-lg border border-stone-200 px-4 py-2.5 text-sm font-medium">
            <SlidersHorizontal size={16} /> Filters {activeCount > 0 && `(${activeCount})`}
          </button>
          <select value={get("sort", "newest")} onChange={(e) => update({ sort: e.target.value })} data-testid="sort-select"
            className="rounded-lg border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-brand bg-white">
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[290px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white rounded-2xl border border-stone-200 p-6">
            <FilterPanel />
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl border border-stone-200 overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-stone-100" />
                  <div className="p-5 space-y-3"><div className="h-5 bg-stone-100 rounded w-1/2" /><div className="h-4 bg-stone-100 rounded w-3/4" /></div>
                </div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-stone-300 rounded-2xl" data-testid="no-results">
              <MapPin className="mx-auto text-stone-300 mb-4" size={40} />
              <p className="font-heading text-xl text-stone-800">No properties match your filters</p>
              <p className="text-stone-500 mt-1">Try adjusting or clearing your filters.</p>
              <button onClick={clearAll} className="mt-5 rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold">Clear filters</button>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="property-grid">
                {data.items.map((p) => <PropertyCard key={p._id} p={p} />)}
              </div>
              {data.pages > 1 && (
                <div className="flex justify-center gap-2 mt-12" data-testid="pagination">
                  {Array.from({ length: data.pages }, (_, i) => i + 1).map((pageNum) => (
                    <button key={pageNum} onClick={() => update({ page: String(pageNum) })} data-testid={`page-${pageNum}`}
                      className={`w-10 h-10 rounded-lg text-sm font-medium border transition-colors ${
                        data.page === pageNum ? "bg-brand text-white border-brand" : "border-stone-200 text-stone-600 hover:border-brand"}`}>
                      {pageNum}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile filters drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-[88%] max-w-sm bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl">Filters</h3>
              <button onClick={() => setShowFilters(false)} data-testid="close-filters"><X /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setShowFilters(false)} className="mt-6 w-full rounded-full bg-brand text-white py-3 font-semibold">
              Show {data.total} results
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
